/**
 * iurop-collection.js — PLP + búsqueda Laganini
 * Panel "Filtrar y Ordenar" (cajón en móvil, columna acoplada en escritorio),
 * grupos colapsables (nacen abiertos), slider doble de precio de escala LINEAL
 * y filtrado AJAX vía Section Rendering API con pushState.
 * Carga progresiva automática al acercarse al final del grid.
 *
 * Sin Search & Discovery no hay facets nativos: talla, color, disponibilidad y
 * precio se filtran EN CLIENTE sobre las celdas del grid (data-f-*). Dos cosas
 * que eso obliga a resolver aquí:
 *  1) el estado vive en la URL (ft/fc/fa/fp) para que sobreviva a la navegación,
 *     al botón atrás y a compartir el enlace;
 *  2) al activar un filtro se cargan TODAS las páginas de la colección por
 *     Section Rendering API y se filtra sobre el conjunto entero, porque
 *     filtrar solo la página visible daba resultados que "crecían" al paginar.
 * Delegación de eventos en la sección (sobrevive a los swaps de innerHTML).
 */
(function () {
  'use strict';

  var initCollection = function (section) {
  if (!section || section.iuropCollectionCleanup) return;
  var cleanup = [];
  var listen = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    cleanup.push(function () { target.removeEventListener(type, handler, options); });
  };
  section.iuropCollectionCleanup = function () {
    cleanup.forEach(function (dispose) { dispose(); });
    if (abortCtrl) abortCtrl.abort();
    clearTimeout(submitTimer);
    clearTimeout(priceTimer);
    if (infiniteObserver) infiniteObserver.disconnect();
    if (familyResizeObserver) familyResizeObserver.disconnect();
    if (section.classList.contains('is-filters-open')) document.body.classList.remove('laganini-filters-open');
    delete section.iuropCollectionCleanup;
  };

  var sectionId = section.getAttribute('data-section-id');
  var DESKTOP_QUERY = '(min-width: 990px)';
  var CLIENT_PARAMS = ['ft', 'fc', 'fa', 'fp'];
  // Tope de peticiones del "cargar todo": por encima se filtra solo la página
  // visible y se conserva la paginación (con los filtros en el enlace).
  var MAX_FULLSET_PAGES = 12;

  var abortCtrl = null;
  var submitTimer = null;
  var priceTimer = null;
  var infiniteObserver = null;
  var infiniteLoading = false;
  var restoreRunning = false;

  var qs = function (sel, root) { return (root || document).querySelector(sel); };
  var qsa = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  var clientF = { talla: [], color: [], avail: false, price: null };
  var fullSet = { loaded: false, promise: null };
  var GRID_KEY = 'laganini:collection-columns';

  var setGridColumns = function (columns, persist) {
    var value = [3, 4, 6].indexOf(columns) !== -1 ? columns : 4;
    if (section.hasAttribute('data-collection-collage')) value = Math.max(2, Math.min(6, parseInt(section.getAttribute('data-default-grid-columns'), 10) || 4));
    qsa('[data-collection-grid]', section).forEach(function (grid) {
      grid.style.setProperty('--plp-cols', String(value));
    });
    qsa('[data-grid-columns]', section).forEach(function (btn) {
      var active = parseInt(btn.getAttribute('data-grid-columns'), 10) === value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (persist) {
      try { window.localStorage.setItem(GRID_KEY, String(value)); } catch (_) { /* noop */ }
    }
  };

  var restoreGridColumns = function () {
    var saved = parseInt(section.getAttribute('data-default-grid-columns'), 10) || 4;
    try {
      var stored = parseInt(window.localStorage.getItem(GRID_KEY), 10);
      if ([3, 4, 6].indexOf(stored) !== -1) saved = stored;
    } catch (_) { /* noop */ }
    setGridColumns(saved, false);
  };

  // ---- Cajón / columna de filtros -----------------------------------------
  // En escritorio la columna puede nacer abierta por CSS (laganini-plp--docked-open,
  // que pone Liquid); ese estado cuenta como "abierto" para el toggle.
  var isDesktop = function () { return window.matchMedia(DESKTOP_QUERY).matches; };

  var isDrawerOpen = function () {
    if (section.classList.contains('is-filters-open')) return true;
    return section.classList.contains('laganini-plp--docked-open') && isDesktop();
  };

  var isDocked = function () { return isDesktop() && !!qs('.laganini-plp__layout', section); };

  var openDrawer = function (moveFocus) {
    section.classList.add('is-filters-open');
    // Solo el cajón overlay bloquea el scroll de página; la columna no
    if (!isDocked()) document.body.classList.add('laganini-filters-open');
    var toggle = qs('[data-filters-toggle]', section);
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (moveFocus === false) return;
    var panel = qs('[data-filters-panel]', section);
    if (panel) {
      var focusable = panel.querySelector('button, input, a');
      if (focusable) focusable.focus({ preventScroll: true });
    }
  };

  var closeDrawer = function () {
    section.classList.remove('is-filters-open');
    section.classList.remove('laganini-plp--docked-open');
    document.body.classList.remove('laganini-filters-open');
    var toggle = qs('[data-filters-toggle]', section);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  };

  // ---- Estado de los filtros en cliente en la URL --------------------------
  var readClientFromUrl = function () {
    var p = new URLSearchParams(window.location.search);
    var list = function (key) {
      var raw = p.get(key);
      if (!raw) return [];
      return raw.split(',').filter(function (v) { return v !== ''; });
    };
    clientF.talla = list('ft');
    clientF.color = list('fc');
    clientF.avail = p.get('fa') === '1';
    clientF.price = null;
    var fp = p.get('fp');
    if (fp && fp.indexOf('-') > 0) {
      var lo = parseFloat(fp.split('-')[0]);
      var hi = parseFloat(fp.split('-')[1]);
      if (!isNaN(lo) && !isNaN(hi) && hi >= lo) clientF.price = { min: lo, max: hi };
    }
  };

  var clientParams = function () {
    var p = new URLSearchParams();
    if (clientF.talla.length) p.set('ft', clientF.talla.join(','));
    if (clientF.color.length) p.set('fc', clientF.color.join(','));
    if (clientF.avail) p.set('fa', '1');
    if (clientF.price) p.set('fp', clientF.price.min + '-' + clientF.price.max);
    return p;
  };

  var clientActive = function () {
    return !!(clientF.talla.length || clientF.color.length || clientF.avail || clientF.price);
  };

  var stripClient = function (params) {
    CLIENT_PARAMS.forEach(function (k) { params.delete(k); });
    return params;
  };

  var withClient = function (params) {
    stripClient(params);
    clientParams().forEach(function (v, k) { params.set(k, v); });
    return params;
  };

  var syncUrl = function () {
    var p = withClient(new URLSearchParams(window.location.search));
    var qstr = p.toString();
    history.replaceState(history.state, '', window.location.pathname + (qstr ? '?' + qstr : ''));
  };

  // El enlace técnico de la siguiente página lo pinta el servidor sin nuestros
  // parámetros. Los conserva aunque la paginación visible ya no exista.
  var syncPaginationLinks = function () {
    qsa('[data-collection-next][href]', section).forEach(function (a) {
      var url;
      try { url = new URL(a.getAttribute('href'), window.location.origin); } catch (e) { return; }
      withClient(url.searchParams);
      a.setAttribute('href', url.pathname + (url.search ? url.search : ''));
    });
  };

  // ---- Slider de precio (lineal entre el mínimo y el máximo reales) --------
  var sliderBounds = function (root) {
    var lo = parseFloat(root.getAttribute('data-min'));
    var hi = parseFloat(root.getAttribute('data-max'));
    return { lo: isNaN(lo) ? 0 : lo, hi: isNaN(hi) ? 0 : hi };
  };

  var paintSlider = function (root) {
    if (!root) return;
    var min = qs('[data-price-range="min"]', root);
    var max = qs('[data-price-range="max"]', root);
    if (!min || !max) return;
    var b = sliderBounds(root);
    var lo = parseFloat(min.value); if (isNaN(lo)) lo = b.lo;
    var hi = parseFloat(max.value); if (isNaN(hi)) hi = b.hi;
    if (lo > hi) { lo = hi; min.value = String(lo); }

    var span = b.hi - b.lo;
    var pct = function (v) { return span > 0 ? ((v - b.lo) / span) * 100 : 0; };
    var fill = qs('[data-price-fill]', root);
    if (fill) {
      fill.style.left = pct(lo) + '%';
      fill.style.right = (100 - pct(hi)) + '%';
    }

    var nMin = qs('[data-price-num="min"]', root);
    var nMax = qs('[data-price-num="max"]', root);
    if (nMin && nMin !== document.activeElement) nMin.value = String(Math.round(lo));
    if (nMax && nMax !== document.activeElement) nMax.value = String(Math.round(hi));

    var hMin = qs('[data-price-input="min"]', root);
    var hMax = qs('[data-price-input="max"]', root);
    if (hMin) hMin.value = lo > b.lo ? String(Math.round(lo)) : '';
    if (hMax) hMax.value = hi < b.hi ? String(Math.round(hi)) : '';
  };

  var initSlider = function (root) {
    var min = qs('[data-price-range="min"]', root);
    var max = qs('[data-price-range="max"]', root);
    if (!min || !max) return;
    var b = sliderBounds(root);
    var lo = parseFloat(root.getAttribute('data-init-min')); if (isNaN(lo)) lo = b.lo;
    var hi = parseFloat(root.getAttribute('data-init-max')); if (isNaN(hi)) hi = b.hi;
    if (root.hasAttribute('data-price-client') && clientF.price) {
      lo = Math.max(b.lo, clientF.price.min);
      hi = Math.min(b.hi, clientF.price.max);
    }
    min.value = String(lo);
    max.value = String(hi);
    paintSlider(root);
  };

  var paintAllSliders = function () { qsa('[data-price-slider]', section).forEach(initSlider); };

  var applyNumInputs = function (root, commit) {
    if (!root) return;
    var min = qs('[data-price-range="min"]', root);
    var max = qs('[data-price-range="max"]', root);
    if (!min || !max) return;
    var b = sliderBounds(root);
    var nMin = qs('[data-price-num="min"]', root);
    var nMax = qs('[data-price-num="max"]', root);
    var lo = parseFloat(nMin && nMin.value); if (isNaN(lo)) lo = b.lo;
    var hi = parseFloat(nMax && nMax.value); if (isNaN(hi)) hi = b.hi;
    lo = Math.max(b.lo, Math.min(lo, b.hi));
    hi = Math.max(b.lo, Math.min(hi, b.hi));
    if (commit && lo > hi) { var t = lo; lo = hi; hi = t; }
    min.value = String(lo);
    max.value = String(hi);
    paintSlider(root);
    if (!commit) return;
    if (nMin) nMin.value = String(Math.round(lo));
    if (nMax) nMax.value = String(Math.round(hi));
  };

  var commitClientPrice = function (root) {
    if (!root || !root.hasAttribute('data-price-client')) return;
    var min = qs('[data-price-range="min"]', root);
    var max = qs('[data-price-range="max"]', root);
    if (!min || !max) return;
    var b = sliderBounds(root);
    var lo = parseFloat(min.value); if (isNaN(lo)) lo = b.lo;
    var hi = parseFloat(max.value); if (isNaN(hi)) hi = b.hi;
    clientF.price = (lo <= b.lo && hi >= b.hi) ? null : { min: Math.round(lo), max: Math.round(hi) };
  };

  // ---- Filtrado en cliente -------------------------------------------------
  // Las celdas de la PLP publican data-f-talla (solo tallas CON stock),
  // data-f-color, data-f-avail y data-f-price. En búsqueda esas celdas no los
  // traen: se cae a los de la card y lo que no exista no filtra.
  var cellAttr = function (cell, name) {
    var v = cell.getAttribute(name);
    if (v !== null) return v;
    var card = cell.querySelector('[data-product-card]');
    return card ? card.getAttribute(name) : null;
  };

  var matchList = function (cell, name, selected) {
    if (!selected.length) return true;
    var raw = cellAttr(cell, name);
    if (raw === null) return true;
    var vals = raw.split('|');
    return selected.some(function (v) { return vals.indexOf(v) > -1; });
  };

  var matchAvail = function (cell) {
    if (!clientF.avail) return true;
    var v = cellAttr(cell, 'data-f-avail');
    return v === null ? true : v === '1';
  };

  var matchPrice = function (cell) {
    if (!clientF.price) return true;
    var raw = cellAttr(cell, 'data-f-price');
    if (raw === null) return true;
    var cents = parseInt(raw, 10);
    if (isNaN(cents)) return true;
    // El extremo alto se redondea hacia arriba: con el tope en 100, 100,90 entra.
    return cents >= clientF.price.min * 100 && cents <= (clientF.price.max + 1) * 100 - 1;
  };

  var emptyMessage = function (show, grid) {
    var msg = qs('[data-client-empty]', section);
    if (!msg) {
      if (!show || !grid) return;
      msg = document.createElement('p');
      msg.setAttribute('data-client-empty', '');
      msg.className = 'laganini-plp__empty';
      msg.textContent = grid.getAttribute('data-no-matches-text') || '';
      grid.parentNode.insertBefore(msg, grid.nextSibling);
    }
    msg.hidden = !show;
  };

  // Independent vertical tracks let products continue beside a tall editorial image.
  var collageFrame = 0;
  var collageObserver = null;
  var collageObserved = new Set();
  var layoutCollage = function () {
    if (!section.hasAttribute('data-collection-collage') || !section.isConnected) return;
    var grid = qs('[data-collection-grid]', section);
    if (!grid || !grid.clientWidth) return;
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    if (section.getAttribute('data-collage-composition') === 'aligned') {
      grid.classList.remove('is-collage-packed');
      grid.classList.add('is-collage-aligned');
      var alignedGridStyle = window.getComputedStyle(grid);
      var columnCount = alignedGridStyle.gridTemplateColumns.trim().split(/\s+/).length;
      var visibleProducts = qsa('.laganini-plp__cell', grid).filter(function (cell) { return !cell.hidden; });
      var productCount = visibleProducts.length;
      var productHeights = visibleProducts.map(function (cell) {
        return cell.getBoundingClientRect().height;
      }).filter(function (height) { return height > 0; }).sort(function (a, b) { return a - b; });
      var productRowHeight = productHeights.length ? productHeights[Math.floor(productHeights.length / 2)] : 0;
      var alignedRowGap = Math.max(0, parseFloat(alignedGridStyle.rowGap) || 0);
      var productsBefore = 0;
      Array.prototype.forEach.call(grid.children, function (item) {
        item.style.removeProperty('grid-column');
        item.style.removeProperty('grid-row');
        if (!item.hasAttribute('data-collage-tile')) {
          if (!item.hidden) productsBefore += 1;
          return;
        }
        var span = parseInt(window.getComputedStyle(item).getPropertyValue(mobile ? '--collage-span-mobile' : '--collage-span'), 10) || 2;
        // Full-width/mobile images keep their own ratio. Sparse filtered results
        // must not depend on two otherwise empty product rows for their height.
        var adjacent = columnCount - span;
        var precedingInRow = Math.min(adjacent, productsBefore % columnCount);
        var doubleRow = !mobile && adjacent > 0 && productCount - productsBefore + precedingInRow >= adjacent * 2;
        item.classList.toggle('is-editorial-double', doubleRow);
        if (doubleRow && productRowHeight > 0) {
          var editorialHeight = Math.round(productRowHeight * 2 + alignedRowGap) + 'px';
          if (item.style.height !== editorialHeight) item.style.height = editorialHeight;
        } else {
          item.style.removeProperty('height');
        }
      });
      return;
    }
    grid.classList.remove('is-collage-aligned');
    grid.classList.add('is-collage-packed');
    var gridStyle = window.getComputedStyle(grid);
    var columns = gridStyle.gridTemplateColumns.trim().split(/\s+/).length;
    var gap = Math.max(0, parseFloat(gridStyle.getPropertyValue('--collage-active-gap')) || 0);
    var items = Array.prototype.slice.call(grid.children);
    var visible = [];
    items.forEach(function (item) {
      var style = window.getComputedStyle(item);
      item.style.removeProperty('height');
      if (item.hidden || style.display === 'none') {
        item.style.removeProperty('grid-column');
        item.style.removeProperty('grid-row');
        return;
      }
      var span = item.hasAttribute('data-collage-tile')
        ? parseInt(style.getPropertyValue(mobile ? '--collage-span-mobile' : '--collage-span'), 10) || 2
        : 1;
      span = Math.max(1, Math.min(columns, span));
      // Set the responsive width before measuring the image and product text.
      item.style.gridColumn = 'span ' + span;
      item.style.gridRow = 'auto';
      visible.push({ element: item, span: span });
    });
    var heights = visible.map(function (item) {
      return Math.max(1, Math.ceil(item.element.getBoundingClientRect().height));
    });
    var ends = Array(columns).fill(0);
    var lastTop = 0;
    visible.forEach(function (item, index) {
      var column = 0;
      var top = Infinity;
      for (var start = 0; start <= columns - item.span; start += 1) {
        var candidate = Math.max.apply(null, [lastTop].concat(ends.slice(start, start + item.span)));
        if (candidate < top) { top = candidate; column = start; }
      }
      top = Math.ceil(top);
      item.element.style.gridColumn = (column + 1) + ' / span ' + item.span;
      item.element.style.gridRow = (top + 1) + ' / span ' + heights[index];
      for (var c = column; c < column + item.span; c += 1) ends[c] = top + heights[index] + gap;
      lastTop = top;
    });
    if (collageObserver) {
      var targets = new Set([grid].concat(items));
      collageObserved.forEach(function (item) {
        if (!targets.has(item)) { collageObserver.unobserve(item); collageObserved.delete(item); }
      });
      targets.forEach(function (item) {
        if (!collageObserved.has(item)) { collageObserver.observe(item); collageObserved.add(item); }
      });
    }
  };
  var scheduleCollage = function () {
    if (collageFrame) cancelAnimationFrame(collageFrame);
    collageFrame = requestAnimationFrame(function () { collageFrame = 0; layoutCollage(); });
  };
  if (section.hasAttribute('data-collection-collage')) {
    if ('ResizeObserver' in window) collageObserver = new ResizeObserver(scheduleCollage);
    listen(window, 'resize', scheduleCollage);
    listen(section, 'load', scheduleCollage, true);
    if (document.fonts) listen(document.fonts, 'loadingdone', scheduleCollage);
    cleanup.push(function () {
      cancelAnimationFrame(collageFrame);
      if (collageObserver) collageObserver.disconnect();
      collageObserved.clear();
    });
  }

  var syncCollage = function (grid) {
    if (!section.hasAttribute('data-collection-collage')) return;
    scheduleCollage();
    qsa('[data-collage-tile]', grid).forEach(function (tile) { tile.remove(); });
    var cells = qsa('.laganini-plp__cell', grid).filter(function (cell) { return !cell.hidden; });
    if (!cells.length) return;
    var sources = qsa('[data-collage-source]', section).map(function (source) {
      return {
        source: source,
        after: parseInt(source.getAttribute('data-after-products'), 10)
      };
    }).filter(function (item) {
      return Number.isFinite(item.after) && item.after >= 0;
    });
    if (!sources.length) return;

    var repeatPattern = section.hasAttribute('data-collage-repeat-pattern');
    var patternLength = sources.reduce(function (max, item) {
      return Math.max(max, item.after);
    }, 0);
    var insertSource = function (item, cycleStart, allowAtEnd) {
      var position = cycleStart + item.after;
      if (position > cells.length || (!allowAtEnd && position === cells.length)) return;
      grid.insertBefore(item.source.content.cloneNode(true), cells[position] || null);
    };

    if (!repeatPattern || patternLength < 1) {
      sources.forEach(function (item) { insertSource(item, 0, true); });
      return;
    }

    for (var cycleStart = 0; cycleStart < cells.length; cycleStart += patternLength) {
      sources.forEach(function (item) { insertSource(item, cycleStart, false); });
    }
  };

  var applyClientFilters = function () {
    var grid = qs('[data-collection-grid]', section);
    if (!grid) return;
    var cells = qsa('.laganini-plp__cell', grid);
    if (!cells.length) { syncCollage(grid); return; }

    var active = clientActive();
    var visible = 0;

    cells.forEach(function (cell) {
      var show;
      if (active) {
        show = matchList(cell, 'data-f-talla', clientF.talla) &&
          matchList(cell, 'data-f-color', clientF.color) &&
          matchAvail(cell) &&
          matchPrice(cell);
      } else {
        // Sin filtros se muestran todas las páginas que ya haya incorporado la
        // carga progresiva.
        show = true;
      }
      cell.hidden = !show;
      if (show) visible += 1;
    });

    syncCollage(grid);
    emptyMessage(active && visible === 0, grid);

    var infinite = qs('[data-infinite-scroll]', section);
    if (infinite && active && fullSet.loaded) infinite.hidden = true;
  };

  // ---- Carga de la colección entera (para filtrar sobre TODO) --------------
  var basePath = function () {
    var form = qs('[data-filters-form]', section);
    return (form && form.getAttribute('action')) || window.location.pathname;
  };

  var nativeQuery = function () {
    var p = stripClient(new URLSearchParams(window.location.search));
    p.delete('page');
    p.delete('section_id');
    return p;
  };

  var ensureFullSet = function () {
    if (fullSet.loaded) return Promise.resolve();
    if (fullSet.promise) return fullSet.promise;

    var grid = qs('[data-collection-grid]', section);
    if (!grid) return Promise.resolve();
    var total = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    var current = parseInt(grid.getAttribute('data-page'), 10) || 1;
    if (total <= 1) { fullSet.loaded = true; return Promise.resolve(); }
    if (total > MAX_FULLSET_PAGES) return Promise.resolve();

    var byPage = {};
    qsa('.laganini-plp__cell', grid).forEach(function (cell) {
      var page = parseInt(cell.getAttribute('data-page'), 10) || current;
      if (!byPage[page]) byPage[page] = [];
      byPage[page].push(cell);
    });

    var base = basePath();
    var params = nativeQuery();
    params.set('section_id', sectionId);

    var jobs = [];
    for (var n = 1; n <= total; n++) {
      if (byPage[n] && byPage[n].length) continue;
      jobs.push((function (page) {
        var p = new URLSearchParams(params.toString());
        p.set('page', String(page));
        return fetch(base + '?' + p.toString(), { headers: { Accept: 'text/html' } })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            return { page: page, cells: qsa('[data-collection-grid] .laganini-plp__cell', doc) };
          })
          .catch(function () { return { page: page, cells: [] }; });
      })(n));
    }

    grid.setAttribute('aria-busy', 'true');
    grid.style.opacity = '0.5';

    fullSet.promise = Promise.all(jobs).then(function (chunks) {
      if (!section.isConnected) return;
      chunks.forEach(function (chunk) {
        if (!chunk.cells.length) return;
        byPage[chunk.page] = chunk.cells.map(function (el) { return document.importNode(el, true); });
      });
      var frag = document.createDocumentFragment();
      Object.keys(byPage)
        .map(Number)
        .sort(function (a, b) { return a - b; })
        .forEach(function (page) {
          byPage[page].forEach(function (el) { frag.appendChild(el); });
        });
      grid.innerHTML = '';
      grid.appendChild(frag);
      grid.removeAttribute('aria-busy');
      grid.style.opacity = '';
      fullSet.loaded = true;
      fullSet.promise = null;
      var infinite = qs('[data-infinite-scroll]', section);
      var next = qs('[data-collection-next]', section);
      if (infinite) infinite.hidden = true;
      if (next) next.removeAttribute('href');
      document.dispatchEvent(new CustomEvent('iurop:product-list:rendered'));
    }).catch(function () {
      grid.removeAttribute('aria-busy');
      grid.style.opacity = '';
      fullSet.promise = null;
    });

    return fullSet.promise;
  };

  // ---- Carga progresiva ----------------------------------------------------
  var maxLoadedPage = function () {
    var max = 1;
    qsa('[data-collection-grid] .laganini-plp__cell', section).forEach(function (cell) {
      var page = parseInt(cell.getAttribute('data-page'), 10);
      if (!isNaN(page)) max = Math.max(max, page);
    });
    return max;
  };

  var updateInfiniteNext = function (doc) {
    var marker = qs('[data-infinite-scroll]', section);
    var current = qs('[data-collection-next]', section);
    var incoming = doc && doc.querySelector('[data-collection-next][href]');
    if (marker && current && incoming) {
      current.setAttribute('href', incoming.getAttribute('href'));
      marker.hidden = false;
      syncPaginationLinks();
      return;
    }
    if (current) current.removeAttribute('href');
    if (marker) marker.hidden = true;
  };

  var loadNextPage = function () {
    if (infiniteLoading || fullSet.loaded) return Promise.resolve(false);
    var link = qs('[data-collection-next][href]', section);
    var grid = qs('[data-collection-grid]', section);
    if (!link || !grid) return Promise.resolve(false);

    var requestUrl;
    try { requestUrl = new URL(link.getAttribute('href'), window.location.origin); } catch (_) { return Promise.resolve(false); }
    CLIENT_PARAMS.forEach(function (key) { requestUrl.searchParams.delete(key); });
    requestUrl.searchParams.set('section_id', sectionId);

    infiniteLoading = true;
    var marker = qs('[data-infinite-scroll]', section);
    if (marker) marker.classList.add('is-loading');
    grid.setAttribute('aria-busy', 'true');

    return fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Collection page request failed');
        return response.text();
      })
      .then(function (html) {
        if (!section.isConnected) return false;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextGrid = doc.querySelector('[data-collection-grid]');
        if (!nextGrid) throw new Error('Collection grid missing');
        var cells = qsa('.laganini-plp__cell', nextGrid);
        var frag = document.createDocumentFragment();
        cells.forEach(function (cell) { frag.appendChild(document.importNode(cell, true)); });
        grid.appendChild(frag);
        grid.setAttribute('data-loaded-page', nextGrid.getAttribute('data-page') || String(maxLoadedPage()));
        updateInfiniteNext(doc);
        applyClientFilters();
        document.dispatchEvent(new CustomEvent('iurop:product-list:rendered'));
        return cells.length > 0;
      })
      .catch(function () {
        return false;
      })
      .then(function (loaded) {
        infiniteLoading = false;
        grid.removeAttribute('aria-busy');
        if (marker) marker.classList.remove('is-loading');
        if (!restoreRunning) refreshInfiniteScroll();
        return loaded;
      });
  };

  var refreshInfiniteScroll = function () {
    if (infiniteObserver) infiniteObserver.disconnect();
    infiniteObserver = null;
    var marker = qs('[data-infinite-scroll]', section);
    var next = qs('[data-collection-next][href]', section);
    if (!marker || !next || clientActive() || fullSet.loaded) {
      if (marker && (!next || fullSet.loaded)) marker.hidden = true;
      return;
    }
    marker.hidden = false;
    if (!('IntersectionObserver' in window)) {
      loadNextPage();
      return;
    }
    infiniteObserver = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      loadNextPage();
    }, { rootMargin: '900px 0px' });
    infiniteObserver.observe(marker);
  };

  // ---- Regreso desde una ficha de producto --------------------------------
  var collectionStateKey = function () {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    var query = params.toString();
    return 'laganini:collection-return:' + window.location.pathname + (query ? '?' + query : '');
  };

  var saveReturnPosition = function (link) {
    var cell = link.closest('.laganini-plp__cell');
    if (!cell) return;
    var productPath = '';
    try { productPath = new URL(link.href, window.location.origin).pathname; } catch (_) { /* noop */ }
    var state = {
      scrollY: window.scrollY,
      cellTop: cell.getBoundingClientRect().top,
      productPath: productPath,
      loadedPage: maxLoadedPage(),
      savedAt: Date.now()
    };
    var key = collectionStateKey();
    try { window.sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) { /* noop */ }
    try {
      var historyState = Object.assign({}, history.state || {});
      historyState.iuropCollectionReturn = key;
      history.replaceState(historyState, '', window.location.href);
    } catch (_) { /* noop */ }
  };

  var readReturnPosition = function () {
    var key = collectionStateKey();
    if (!history.state || history.state.iuropCollectionReturn !== key) return null;
    try {
      var state = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!state || Date.now() - state.savedAt > 21600000) return null;
      return state;
    } catch (_) {
      return null;
    }
  };

  var restoreReturnPosition = function () {
    var saved = readReturnPosition();
    if (!saved || restoreRunning) return Promise.resolve(false);
    restoreRunning = true;

    var loadUntilSavedPage = function () {
      if (maxLoadedPage() >= (parseInt(saved.loadedPage, 10) || 1)) return Promise.resolve();
      if (!qs('[data-collection-next][href]', section)) return Promise.resolve();
      return loadNextPage().then(function (loaded) {
        if (!loaded) return;
        return loadUntilSavedPage();
      });
    };

    return loadUntilSavedPage().then(function () {
      return new Promise(function (resolve) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            var targetCell = null;
            if (saved.productPath) {
              qsa('[data-product-card] a[href]', section).some(function (anchor) {
                try {
                  if (new URL(anchor.href, window.location.origin).pathname !== saved.productPath) return false;
                  targetCell = anchor.closest('.laganini-plp__cell');
                  return !!targetCell;
                } catch (_) {
                  return false;
                }
              });
            }
            if (targetCell && Number.isFinite(saved.cellTop)) {
              window.scrollTo(0, Math.max(0, window.scrollY + targetCell.getBoundingClientRect().top - saved.cellTop));
            } else {
              window.scrollTo(0, Math.max(0, parseFloat(saved.scrollY) || 0));
            }
            restoreRunning = false;
            refreshInfiniteScroll();
            resolve(true);
          });
        });
      });
    });
  };

  var runClientFilters = function () {
    applyClientFilters();
    syncUrl();
    syncPaginationLinks();
    if (!clientActive()) {
      refreshInfiniteScroll();
      return Promise.resolve();
    }
    return ensureFullSet().then(function () {
      applyClientFilters();
      syncPaginationLinks();
      refreshInfiniteScroll();
    });
  };

  var restoreClientState = function () {
    qsa('[data-client-talla]', section).forEach(function (cb) {
      cb.checked = clientF.talla.indexOf(cb.value) > -1;
    });
    qsa('[data-client-color]', section).forEach(function (cb) {
      cb.checked = clientF.color.indexOf(cb.value) > -1;
    });
    qsa('[data-client-avail]', section).forEach(function (cb) {
      cb.checked = clientF.avail;
    });
    paintAllSliders();
  };

  var collectClientList = function (key) {
    return qsa('[data-client-' + key + ']', section)
      .filter(function (cb) { return cb.checked; })
      .map(function (cb) { return cb.value; });
  };

  // ---- Filtrado AJAX de lo que SÍ es nativo (orden, facets S&D) ------------
  var buildQuery = function () {
    var form = qs('[data-filters-form]', section);
    var params = new URLSearchParams();
    if (!form) return params;
    qsa('input, select', form).forEach(function (el) {
      if (!el.name) return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      if (el.type === 'range') return;
      if (el.value === '') return;
      params.append(el.name, el.value);
    });
    return params;
  };

  var applyFilters = function (overrideParams, noPush) {
    var params = overrideParams || buildQuery();
    var path = basePath();
    var fetchParams = stripClient(new URLSearchParams(params.toString()));
    var qstr = fetchParams.toString();
    var publicUrl = path;
    var publicParams = withClient(new URLSearchParams(params.toString()));
    var publicQstr = publicParams.toString();
    if (publicQstr) publicUrl = path + '?' + publicQstr;
    var fetchUrl = path + '?' + (qstr ? qstr + '&' : '') + 'section_id=' + sectionId;

    var grid = qs('[data-collection-grid]', section);
    if (grid) grid.style.opacity = '0.5';

    var collapsedTitles = qsa('[data-filter-group]', section)
      .filter(function (g) { return g.classList.contains('is-collapsed'); })
      .map(function (g) { var t = qs('.laganini-filters__group-title', g); return t ? t.textContent.trim() : ''; });

    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();

    fetch(fetchUrl, { signal: abortCtrl.signal, headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!section.isConnected) return;
        var wasOpen = isDrawerOpen();

        // El panel de filtros vive fuera de [data-collection-body]: swap de ambos.
        ['[data-collection-body]', '[data-filters-panel]'].forEach(function (sel) {
          var next = doc.querySelector(sel);
          var cur = qs(sel, section);
          if (next && cur) cur.innerHTML = next.innerHTML;
        });

        fullSet.loaded = false;
        fullSet.promise = null;

        qsa('[data-filter-group]', section).forEach(function (g) {
          var t = qs('.laganini-filters__group-title', g);
          var title = t ? t.textContent.trim() : '';
          if (collapsedTitles.indexOf(title) > -1) {
            g.classList.add('is-collapsed');
            var head = qs('[data-group-toggle]', g);
            if (head) head.setAttribute('aria-expanded', 'false');
          }
        });
        if (wasOpen && !isDrawerOpen()) openDrawer(false);
        restoreClientState();
        document.dispatchEvent(new CustomEvent('iurop:product-list:rendered'));
        restoreGridColumns();
        var newGrid = qs('[data-collection-grid]', section);
        if (newGrid) newGrid.style.opacity = '';
        // Al volver por popstate NO se empuja otra entrada (bucle de "atrás").
        if (!noPush) history.pushState({ collectionFilters: true }, '', publicUrl);
        runClientFilters();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location.href = publicUrl;
      });
  };

  var debouncedApply = function () {
    clearTimeout(submitTimer);
    submitTimer = setTimeout(function () { applyFilters(); }, 250);
  };

  // ---- Eventos delegados ---------------------------------------------------
  listen(section, 'click', function (e) {
    var productLink = e.target.closest('[data-product-card] a[href*="/products/"]');
    if (productLink && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      saveReturnPosition(productLink);
    }

    var gridButton = e.target.closest('[data-grid-columns]');
    if (gridButton) {
      setGridColumns(parseInt(gridButton.getAttribute('data-grid-columns'), 10), true);
      return;
    }

    var groupHead = e.target.closest('[data-group-toggle]');
    if (groupHead && section.contains(groupHead)) {
      var group = groupHead.closest('[data-filter-group]');
      if (group) {
        var collapsed = group.classList.toggle('is-collapsed');
        groupHead.setAttribute('aria-expanded', String(!collapsed));
      }
      return;
    }

    if (e.target.closest('[data-filters-toggle]')) {
      if (isDrawerOpen()) closeDrawer(); else openDrawer(!isDesktop());
      return;
    }
    if (e.target.closest('[data-filters-close]') || e.target.closest('[data-filters-overlay]')) {
      closeDrawer();
      return;
    }

    var clear = e.target.closest('[data-filters-clear]');
    if (clear && qs('[data-filters-form]', section)) {
      e.preventDefault();
      clientF.talla = [];
      clientF.color = [];
      clientF.avail = false;
      clientF.price = null;
      var keep = new URLSearchParams();
      var form = qs('[data-filters-form]', section);
      // Conserva q/type en la búsqueda al limpiar filtros.
      qsa('input[type="hidden"][name="q"], input[type="hidden"][name="type"]', form).forEach(function (el) {
        if (el.value !== '') keep.append(el.name, el.value);
      });
      applyFilters(keep);
      return;
    }
  });

  listen(document, 'keydown', function (e) {
    if (e.key === 'Escape' && isDrawerOpen()) {
      closeDrawer();
      var toggle = qs('[data-filters-toggle]', section);
      if (toggle) toggle.focus({ preventScroll: true });
    }
  });

  listen(section, 'submit', function (e) {
    if (e.target.closest('[data-filters-form]')) {
      e.preventDefault();
      qsa('[data-price-slider]', section).forEach(function (r) {
        applyNumInputs(r, true);
        commitClientPrice(r);
      });
      clearTimeout(submitTimer);
      if (qs('[data-price-slider]:not([data-price-client])', section)) applyFilters();
      else runClientFilters();
    }
  });

  listen(section, 'input', function (e) {
    if (e.target.matches('[data-price-range]')) {
      paintSlider(e.target.closest('[data-price-slider]'));
    } else if (e.target.matches('[data-price-num]')) {
      applyNumInputs(e.target.closest('[data-price-slider]'), false);
    }
  });

  listen(section, 'change', function (e) {
    var t = e.target;
    if (!t.closest('[data-filters-form]')) return;

    // Talla, color, disponibilidad y precio (modo cliente) NO recargan la
    // sección: van antes del matcher genérico de checkboxes.
    if (t.matches('[data-client-talla], [data-client-color]')) {
      var key = t.matches('[data-client-talla]') ? 'talla' : 'color';
      clientF[key] = collectClientList(key);
      runClientFilters();
      return;
    }
    if (t.matches('[data-client-avail]')) {
      clientF.avail = t.checked;
      runClientFilters();
      return;
    }
    if (t.matches('[data-price-range], [data-price-num]')) {
      var root = t.closest('[data-price-slider]');
      if (t.matches('[data-price-num]')) applyNumInputs(root, true);
      else paintSlider(root);
      if (root && root.hasAttribute('data-price-client')) {
        commitClientPrice(root);
        clearTimeout(priceTimer);
        priceTimer = setTimeout(runClientFilters, 200);
      } else {
        debouncedApply();
      }
      return;
    }
    if (t.matches('.laganini-filters__checkbox, .laganini-filters__radio')) { applyFilters(); }
  });

  listen(window, 'popstate', function () {
    readClientFromUrl();
    applyFilters(stripClient(new URLSearchParams(window.location.search)), true);
  });

  // Illustrated family navigation stays independent of catalog filtering.
  var familyCarousel = qs('[data-family-carousel]', section);
  if (familyCarousel) {
    var familyTrack = qs('[data-family-track]', familyCarousel);
    var familyPrev = qs('[data-family-prev]', familyCarousel);
    var familyNext = qs('[data-family-next]', familyCarousel);
    var categoryTrack = qs('.laganini-plp__cats-track', section);
    var syncFamilyWidth = function () {
      if (!categoryTrack || !categoryTrack.children.length) return;
      var first = categoryTrack.firstElementChild.getBoundingClientRect();
      var last = categoryTrack.lastElementChild.getBoundingClientRect();
      var width = Math.max(first.right, last.right) - Math.min(first.left, last.left);
      var widthMultiplier = familyTrack.children.length >= 3 ? 1.3 : 1;
      if (width > 0) familyCarousel.style.setProperty('--family-nav-width', Math.ceil(width * widthMultiplier) + 'px');
    };
    var updateFamilyArrows = function () {
      var max = Math.max(0, familyTrack.scrollWidth - familyTrack.clientWidth);
      familyPrev.disabled = familyTrack.scrollLeft <= 1;
      familyNext.disabled = familyTrack.scrollLeft >= max - 1;
    };
    var familyStep = function (direction) {
      var item = familyTrack.firstElementChild;
      if (!item) return;
      var step = item.getBoundingClientRect().width + parseFloat(getComputedStyle(familyTrack).columnGap);
      familyTrack.scrollBy({ left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    listen(familyPrev, 'click', function () { familyStep(-1); });
    listen(familyNext, 'click', function () { familyStep(1); });
    listen(familyTrack, 'scroll', updateFamilyArrows, { passive: true });
    var layoutFamilies = function () {
      syncFamilyWidth();
      var width = familyCarousel.clientWidth;
      if (!width) return;
      var minWidth = parseFloat(getComputedStyle(familyCarousel).getPropertyValue('--family-min-width')) || 128;
      var gap = parseFloat(getComputedStyle(familyTrack).columnGap) || 0;
      var count = familyTrack.children.length;
      var visibleColumns = Math.min(3, count);
      var overflowing = count > visibleColumns;
      familyCarousel.classList.toggle('is-overflowing', overflowing);
      familyPrev.hidden = familyNext.hidden = !overflowing;
      var available = familyTrack.clientWidth;
      var itemWidth = count >= 3 ? (available - gap * 2) / 3 : Math.min(minWidth, available);
      familyTrack.style.setProperty('--family-item-width', itemWidth + 'px');
      var activeFamily = qs('.is-active', familyTrack);
      if (!overflowing) {
        familyTrack.scrollLeft = 0;
      } else if (activeFamily) {
        var trackRect = familyTrack.getBoundingClientRect();
        var activeRect = activeFamily.getBoundingClientRect();
        if (activeRect.right > trackRect.right) familyTrack.scrollLeft += activeRect.right - trackRect.right;
        else if (activeRect.left < trackRect.left) familyTrack.scrollLeft -= trackRect.left - activeRect.left;
      }
      updateFamilyArrows();
    };
    if ('ResizeObserver' in window) {
      var familyResizeObserver = new ResizeObserver(layoutFamilies);
      familyResizeObserver.observe(familyCarousel);
      if (categoryTrack) {
        familyResizeObserver.observe(categoryTrack);
        Array.prototype.forEach.call(categoryTrack.children, function (item) { familyResizeObserver.observe(item); });
      }
    } else {
      listen(window, 'resize', layoutFamilies);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () {
      if (section.isConnected) layoutFamilies();
    });
    layoutFamilies();
  }

  // ---- Init ----------------------------------------------------------------
  readClientFromUrl();
  restoreGridColumns();
  syncCollage(qs('[data-collection-grid]', section));
  restoreClientState();
  syncPaginationLinks();
  var initialDataReady = clientActive() ? runClientFilters() : Promise.resolve();
  initialDataReady.then(restoreReturnPosition).then(refreshInfiniteScroll);
  listen(window, 'pageshow', function (event) {
    if (event.persisted) restoreReturnPosition();
  });
  if (isDrawerOpen()) {
    var initToggle = qs('[data-filters-toggle]', section);
    if (initToggle) initToggle.setAttribute('aria-expanded', 'true');
  }
  };
  document.querySelectorAll('[data-collection-section]').forEach(initCollection);
  if (window.Shopify && window.Shopify.designMode) {
    window.iuropInitCollection = initCollection;
    if (!window.iuropCollectionEditorBound) {
      window.iuropCollectionEditorBound = true;
      document.addEventListener('shopify:section:load', function (event) {
        var sections = event.target.matches('[data-collection-section]') ? [event.target] : event.target.querySelectorAll('[data-collection-section]');
        Array.prototype.forEach.call(sections, window.iuropInitCollection);
      });
      document.addEventListener('shopify:section:unload', function (event) {
        var sections = event.target.matches('[data-collection-section]') ? [event.target] : event.target.querySelectorAll('[data-collection-section]');
        Array.prototype.forEach.call(sections, function (section) {
          if (section.iuropCollectionCleanup) section.iuropCollectionCleanup();
        });
      });
    }
  }
})();
